import { Schema, type, view, MapSchema } from "@colyseus/schema";
import { EquipSlot, BackpackSlot, initCubeInventory } from "./inventory";
import { SurvivalBag } from "./survival";
import { Spellbook } from "./spellbook";
import { Expertise } from "./expertise-schema";
import { Projectile } from "./projectile";

/**
 * Per-cube transient behavior — one row per active buff/debuff.
 *
 * `expiresAt` semantics:
 *   0      = permanent (no auto-expire); explicit removeBehavior clears it.
 *   > 0    = ms-epoch wall-clock when the behavior should auto-expire.
 *
 * Mirrors the server-only WeakMap in survival/transient.ts. Single source of
 * truth: addTransientBehavior writes to both. Renders client-side via
 * floaters/registry.ts (id → presentation lookup); unregistered ids silently
 * skip render. Adding a new behavior id = one registry row + one server-side
 * addTransientBehavior call. Renderer never changes (data-driven, per
 * memory feedback_data_driven_unbuilt_deps).
 *
 * Spec: docs/superpowers/specs/2026-05-03-floating-overhead-ui-design.md §3
 */
export class BehaviorEntry extends Schema {
  @type("number") expiresAt: number = 0;
}

/**
 * Per-cube identity + buff/debuff bundle.
 *
 * Lives on Cube as `cube.meta` so that two semi-related fields (display name
 * and the active-behaviors mirror) cost ONE Cube schema slot instead of two.
 * Background: @colyseus/schema's wire format compresses field-index +
 * operation into a single byte (top 2 bits = operation, bottom 6 bits =
 * index), so any Schema with more than 64 fields silently corrupts the byte
 * stream during decode — adding `behaviors` directly to Cube on
 * feat/floating-overhead-ui pushed Cube to 65 fields and broke the initial
 * snapshot for late-joining clients (own cube invisible, level "NaN", flood
 * of `refId not found` warnings as the misaligned bytes were re-interpreted
 * as DELETE_AND_ADD operations).
 *
 * `name` is a permanent string written once on join; `behaviors` is the
 * sync mirror of survival/transient.ts's WeakMap (writes go through
 * addTransientBehavior which updates both). Renders via
 * floaters/manager.ts + floaters/rich-overlay.ts.
 */
export class KoboldMeta extends Schema {
  @type("string") name: string = "";
  @type({ map: BehaviorEntry }) behaviors = new MapSchema<BehaviorEntry>();
}

/**
 * Per-cube resource counters. Lives on Cube as `cube.resources` (NOT flat fields)
 * because @colyseus/schema imposes a hard 64-field-per-class cap and the Phase-2
 * economy migration (17 raw counters + equipment maps + 7 derived stats + the
 * existing combat block) overflows that cap. Grouping the inventory into a
 * sub-Schema costs 1 Cube field instead of 17 and synchronises identically over
 * the wire (clients see `cubes.<id>.resources.lumber`).
 *
 * Mutation pattern is unchanged from the flat-field MVP — just add one hop:
 *   cube.resources.lumber += 1     // (was: cube.lumber += 1)
 *
 * The 17 counters cover the gathering MVP (lumber/stone/gold/berry/herb,
 * ironOre/wool/leather) plus the 9 KTS recipe-ingredient surfaces added in
 * Phase 2 (infusedLumber, bearPelt, sageberry, spicyHerbs, nightcrawler, fish,
 * ancientArtifactFragment, shadowstone, radiantGem). All default to 0;
 * populating them happens as harvest sources / drops / recipe outputs land.
 */
export class ResourceBag extends Schema {
  // Original gathering MVP counters
  @type("uint16") lumber: number = 0;
  @type("uint16") stone: number = 0;
  @type("uint16") gold: number = 0;
  @type("uint16") berry: number = 0;
  @type("uint16") herb: number = 0;

  // Buildings-spec counters (16-buildings.md §v1 implementation scope)
  @type("uint16") ironOre: number = 0;
  @type("uint16") wool: number = 0;
  @type("uint16") leather: number = 0;

  // Phase-2 economy resources — 9 new counters covering KTS recipe ingredients
  // not already represented above:
  //   - infusedLumber, bearPelt          — Forge/Workbench mats (drops + processed)
  //   - sageberry, spicyHerbs            — Tavern/Cooking herb gardens
  //   - nightcrawler, fish               — Tavern bait + raw food
  //   - ancientArtifactFragment, shadowstone, radiantGem — boss/quest tier mats
  @type("uint16") infusedLumber: number = 0;
  @type("uint16") bearPelt: number = 0;
  @type("uint16") sageberry: number = 0;
  @type("uint16") spicyHerbs: number = 0;
  @type("uint16") nightcrawler: number = 0;
  @type("uint16") fish: number = 0;
  @type("uint16") ancientArtifactFragment: number = 0;
  @type("uint16") shadowstone: number = 0;
  @type("uint16") radiantGem: number = 0;
}

/**
 * Canonical list of ResourceBag field keys. Single source of truth — every
 * other surface (craft cost classification, debug allowlists, snapshot
 * shapes) imports from here. Adding a new counter to ResourceBag means
 * adding the field above AND its key here.
 */
export const RESOURCE_KEYS = [
  "lumber", "stone", "gold", "berry", "herb",
  "ironOre", "wool", "leather", "infusedLumber", "bearPelt",
  "sageberry", "spicyHerbs", "nightcrawler", "fish",
  "ancientArtifactFragment", "shadowstone", "radiantGem",
] as const;

export type ResourceKey = (typeof RESOURCE_KEYS)[number];

/** Uint16 cap for gold + every other ResourceBag counter (all `@type("uint16")`).
 *  Single source of truth — server-side gold credits / pickup transfers / craft
 *  costs all clamp through here so a future schema widening (uint32) flips
 *  exactly one place. */
export const GOLD_MAX = 65535;

/** Day/night transition band in seconds — KTS gv_transitionTime = 20
 *  (MapScript.galaxy:313). The server phase counter flips instantaneously
 *  (day-night.ts); this constant is for the CLIENT lighting lerp to consume
 *  when day/night visuals ship — the dusk/dawn fade should span this window.
 *  Lives in shared/ so server-side gates (e.g. future Foraging windows) and
 *  the client lerp read the same number. */
export const DAY_NIGHT_TRANSITION_SEC = 20;

/** Day / night phase durations in seconds — KTS-verbatim (2026-06-10 tuning sweep).
 *  Day = 140 (MapScript.galaxy:1789), Night = 80. Single source of truth: the
 *  server day-night counter reads these as its default config, and the client
 *  lighting lerp + clock cue read them to compute phase progress + countdowns.
 *  See docs/design/12-day-night.md §"v1 durations". */
export const DAY_DURATION_SEC = 140;
export const NIGHT_DURATION_SEC = 80;

// ⚠️ FIELD CAP: Cube is at 64/64 @colyseus/schema fields — the hard wire-format
// cap. A 65th @type field silently corrupts snapshots (no compile error). Do NOT
// add a flat field: route a transient stat-mod through `mods` (getMod) or nest new
// state under a sub-schema. Enforced by qa-cube-field-cap.ts.
// See docs/build/integration-layer.md#the-schema-field-cap-guardrail.
export class Cube extends Schema {
  @type("string") sessionId!: string;
  @type("number") x: number = 0;
  @type("number") z: number = 0;
  @type("number") targetX: number = 0;
  @type("number") targetZ: number = 0;

  // Inventory (from resource gathering MVP). Counters live on the nested
  // ResourceBag so the Cube schema stays under @colyseus/schema's hard
  // 64-field-per-class cap — Phase 2's 12 economy counters + 7 derived stats
  // would otherwise overflow. Read sites use `cube.resources.lumber`.
  @type(ResourceBag) resources = new ResourceBag();
  @type("uint32") xp: number = 0;
  @type("string") harvestTargetId: string = "";

  // === Combat MVP additions ===
  // Spec: docs/superpowers/specs/2026-04-26-combat-mvp-design.md

  // Core state
  @type("uint16") hp: number = 250;              // TRANSIENT — current HP, mutated by combat / regen
  @type("uint16") hpMax: number = 250;           // DERIVED — written by recomputeDerivedStats
  @type("uint8")  teamId: number = 0;            // 0 or 1; round-robin on join. 99 = debug dummy.
  @type("string") state: string = "alive";       // "alive" | "dead"
  @type("number") respawnAt: number = 0;         // server ms when respawn fires; 0 if alive

  // Sub-stats (Physique + Wit catalog defaults from EffectData.xml). KTS-canonical
  // names — `dexterity` replaced the legacy `focus` alias in Task 2.9 of the
  // economy-foundation Phase 2 (combat.ts and debug/actions.ts updated in lockstep).
  @type("uint8") strength: number = 20;
  @type("uint8") dexterity: number = 18;
  @type("uint8") endurance: number = 25;
  @type("uint8") intelligence: number = 20;
  @type("uint8") willpower: number = 18;

  // Class identity. KTS dispatches several derived-stat formulas off this
  // field (mage gets +35% mana / -10% life; berserker gets +3 armor / +5%
  // melee damage / +15% life regen; etc — see derived-stats.ts). The class
  // system itself is Phase 3+; v1 ships with everyone as type 1 (warrior),
  // so the koboldType branches in derived-stats.ts are inert until classes
  // ship. Default 1 keeps formulas at the warrior baseline.
  //   1 = warrior (default)
  //   2 = mage
  //   3 = berserker
  //   4 = monk
  //   5 = priest
  //   6 = reserved
  @type("uint8") koboldType: number = 1;

  // Parent attributes (KTS Physique = STR+DEX+END, Wit = INT+WIL). DERIVED at
  // construct time from the sub-stat defaults; Task 2.10's recompute-step is
  // responsible for keeping these in sync as sub-stats change.
  @type("uint16") physique: number = 63;       // STR(20) + DEX(18) + END(25) — sums Cube sub-stat defaults
  @type("uint16") wit: number = 38;            // INT(20) + WIL(18)

  // Damage modifier fields — two-layer (base + mod) per KTS KoboldStats struct
  // (MapScript.galaxy:493). Effective value = base + mod. Annotation contract:
  //   DERIVED   — written ONLY by recomputeDerivedStats (rebuilds from sub-stats
  //               + equipped items + buffs). Damage pipeline reads `base + mod`
  //               for the effective value (combat.ts:178+ pattern is preserved).
  //   TRANSIENT — open to perks / debuffs / on-equip mutations; cleared on unequip.
  //   RESERVED  — declared for KTS shape parity but not consumed in v1.
  // Phase 2 invariant: don't write to *base* fields outside recomputeDerivedStats.
  @type("uint16") critChance: number = 0;        // DERIVED — 0–10000 basis points (10000 = 100%)
  @type("uint16") critChanceMod: number = 0;     // TRANSIENT
  @type("uint16") critDamage: number = 0;        // DERIVED — 0–10000 bp; blends dexterity
  @type("uint16") critDamageMod: number = 0;     // TRANSIENT
  @type("int16")  armor: number = 0;             // DERIVED — physical armor; can go negative (amplify)
  @type("int16")  armorMod: number = 0;          // TRANSIENT
  @type("int16")  sArmor: number = 0;            // DERIVED — spell armor
  @type("int16")  sArmorMod: number = 0;         // TRANSIENT
  @type("int16")  armorPen: number = 0;          // DERIVED — physical armor penetration
  @type("int16")  armorPenMod: number = 0;       // TRANSIENT
  @type("int16")  spellPen: number = 0;          // RESERVED — unused at MVP
  @type("int16")  spellPenMod: number = 0;       // TRANSIENT
  @type("uint16") spellChance: number = 0;       // DERIVED
  @type("uint16") spellChanceMod: number = 0;    // TRANSIENT
  @type("int16")  spellBonus: number = 0;        // DERIVED
  @type("int16")  spellBonusMod: number = 0;     // TRANSIENT
  @type("int16")  spellResist: number = 0;       // DERIVED
  @type("int16")  spellResistMod: number = 0;    // TRANSIENT
  @type("uint16") evasion: number = 0;           // RESERVED — plumbed but unused in KTS damage path
  @type("uint16") evasionMod: number = 0;        // TRANSIENT
  // NOTE: the RESERVED spellEvasion/spellEvasionMod pair was dropped 2026-06-11 to
  // free a Cube schema slot for `expertise` (below) under the 64-field cap. Zero
  // readers existed anywhere; re-add here if a spell ever needs spell-evasion.
  @type("int16")  cooldownRate: number = 0;      // DERIVED
  @type("int16")  cooldownRateMod: number = 0;   // TRANSIENT

  // Phase 2.10 — additional DERIVED stat surface needed by abilities, regen,
  // weapon dispatch, and movement systems. All written only by
  // recomputeDerivedStats (lands in a follow-up phase); damage / spell / regen
  // tickers read these as "effective unless a *Mod sibling exists later".
  @type("uint16") attackDamage: number = 0;      // DERIVED — base attack post-physique formula
  @type("uint16") attackSpeed: number = 1000;    // DERIVED — milliseconds (lower = faster)
  @type("uint16") attackRange: number = 1;       // DERIVED — units; weapon-class dependent
  @type("uint16") manaMax: number = 80;          // DERIVED — Wit[Armored] × 4 + bonuses
  @type("int16")  manaRegen: number = 0;         // DERIVED
  @type("int16")  lifeRegen: number = 0;         // DERIVED
  @type("uint16") moveSpeed: number = 250;       // DERIVED — KTS sets via UnitGetPropertyFixed; v1 uses fixed default + multipliers

  // Per-attribute damage bonus tracks (KTS amt/amtAnimal/amtTree/amtStructure). 0 at MVP.
  @type("int16")  attrBonusAnimal: number = 0;
  @type("int16")  attrBonusTree: number = 0;
  @type("int16")  attrBonusStructure: number = 0;

  // Target attribute classification: 0 = Hero (kobold), 1 = Animal, 2 = Tree, 3 = Structure.
  // MVP: all kobolds = 0; creatures will use 1; buildings = 3 when those land.
  @type("uint8")  attrType: number = 0;

  // Validator flags
  @type("boolean") invulnerable: boolean = false;
  @type("boolean") blockNextAttack: boolean = false;

  // Bot AI flag — true when this Cube is driven by the priority cascade
  // (game/server/src/bots/). Toggled by /afk and /unafk chat commands.
  // KTS analog: KoboldBotNormal behavior buff (BehaviorData.xml:9863).
  @type("boolean") isBot: boolean = false;

  // Inventory schema (Phase 2.5+2.6). Maps are pre-seeded by
  // `initCubeInventory(cube)` called from EVERY `new Cube()` site so consumers
  // can iterate slots without lazy-creation guards. Slot keys are stable:
  //   - equipment: "main-hand" | "offhand" | "armor"   (matches ItemSlot enum)
  //   - backpack:  "0" .. "11"                         (12 slots per v10 spec)
  // See game/shared/src/inventory.ts for full design rationale.
  @type({ map: EquipSlot }) equipment = new MapSchema<EquipSlot>();
  @type({ map: BackpackSlot }) backpack = new MapSchema<BackpackSlot>();

  // Survival meters (warmth/hunger/stamina) + threshold-derived factors. Lives in
  // its own sub-Schema to keep Cube under @colyseus/schema's 64-field cap.
  // Spec: docs/superpowers/specs/2026-04-30-survival-design.md §4
  @type(SurvivalBag) survival = new SurvivalBag();

  // walkToggled lives on SurvivalBag (next to stamina) — relocated 2026-05-12
  // because stacking spellbook (abilities branch) + meta (main) onto Cube
  // crossed the @colyseus/schema 64-field cap. See SurvivalBag.walkToggled
  // JSDoc for the full diagnosis. Read sites: cube.survival.walkToggled.

  // Active-ability state (learned spells, Q/W/E/R bindings, cooldowns, in-flight
  // cast staging, Mineral Tap mining-rate buff, current mana pool). Lives in
  // its own sub-Schema to keep Cube under @colyseus/schema's 64-field cap —
  // same pattern as ResourceBag (cube.resources) and SurvivalBag (cube.survival).
  // Note: `manaMax`/`manaRegen` (DERIVED) stay on Cube above; the TRANSIENT
  // `mana` meter lives on Spellbook — mirrors Cube.hp/hpMax vs
  // SurvivalBag.warmth/hunger/stamina.
  // Spec: docs/design/09-abilities.md §"Slots & binding", §"Cast flow".
  @type(Spellbook) spellbook = new Spellbook();

  // Identity + active-behaviors bundle. Lives in a sub-Schema so two related
  // fields (display name + behaviors map) cost ONE Cube slot — keeps Cube
  // under @colyseus/schema's 64-field wire-format cap. See KoboldMeta docstring
  // for the why.
  @type(KoboldMeta) meta = new KoboldMeta();

  // Expertise spend-state (B5 progression): character level, banked points, and
  // the 5 tree investments (0..25 each). Own sub-Schema = one Cube slot (the raw
  // `xp` counter above stays on Cube). Spec: docs/design/05-heroes-progression.md.
  @type(Expertise) expertise = new Expertise();

  // SCALABLE stat-mod bag (2026-06-12). A single nested MapSchema holds an
  // UNLIMITED number of keyed transient stat-mods at the cost of ONE Cube field.
  // This is the durable answer to @colyseus/schema's 64-field-per-class cap:
  // instead of adding a new flat `<x>Mod` field per perk (witMod, attackSpeedMod,
  // …) — each of which eats a scarce Cube slot and forced the earlier
  // `cube.spellbook` cap-hack — new transient mods become MAP KEYS here, costing
  // zero additional schema fields. The legacy flat `*Mod` fields above
  // (armorMod / sArmorMod / armorPenMod) still work and are read directly; every
  // NEW mod routes through this bag via `buffs/stat-mod.ts`. Read via `getMod()`.
  @type({ map: "number" }) mods = new MapSchema<number>();
}

/**
 * Effective value of a map-routed transient stat-mod (0 when absent). The single
 * reader for everything in `Cube.mods` — derived-stats / combat / consumers call
 * this instead of poking the map, so the "absent = 0" contract lives in one place.
 */
export function getMod(cube: Cube, key: string): number {
  return cube.mods.get(key) ?? 0;
}

/**
 * Construct a fully-initialized Cube (equipment + backpack pre-seeded).
 *
 * Production code should ALWAYS use this instead of `new Cube()` so the
 * inventory contract ("every Cube has 3 equipment slots + 8 backpack slots
 * the moment it's instantiated") is structurally enforced rather than
 * relying on every spawn site to remember `initCubeInventory(cube)`.
 *
 * Phase 5 (drop-on-death) reads `cube.backpack` to populate Corpse.items —
 * a missed `initCubeInventory` would silently spawn empty corpses, which is
 * exactly the kind of fragile contract this factory eliminates.
 *
 * Test files (qa-*.ts) may still use `new Cube()` directly when they only
 * need a minimal stub that doesn't touch inventory; qa-inventory-schema.ts
 * specifically tests the `new Cube() + initCubeInventory(c)` pairing so it
 * keeps calling the helper explicitly.
 */
export function createCube(): Cube {
  const c = new Cube();
  initCubeInventory(c);
  return c;
}

// Resource node (unchanged)
export class ResourceNode extends Schema {
  @type("string") id!: string;
  @type("string") kind!: string;
  @type("number") x: number = 0;
  @type("number") z: number = 0;
  @type("uint16") hp: number = 0;
  @type("uint16") hpMax: number = 0;
}

// Neutral creature entity. Server-authoritative; ticks per CreatureAI in
// game/server/src/creatures/. Schema lifted minimal: only fields the client
// needs for rendering/HUD — stats stay server-side via creature-data.ts.
//
// `creatureType` is the wire-format unit name ("Stag" | "Sheep" | "Fox" | future
// "Wolf" | "Bear" | ...). Fox is a mesh-reskin of the KTS Pheasant unit — the
// stat block lifts from KTS Pheasant but the wire string is "Fox" so the client
// mesh-mapper resolves to the Fox glTF. The client maps type → mesh in
// creature-mesh.ts.
//
// `aiState` drives client animation choice:
//   "idle"    → idle clip
//   "wander"  → walk clip (slow ambient meander, future)
//   "flee"    → run clip (sprint after StagEvasion)
//   "chase"   → run clip (Plan 2: neutral-retaliate / hostile-hunt)
//   "attack"  → attack clip + auto-revert (Plan 2)
//   "dead"    → death clip + freeze
export class Creature extends Schema {
  @type("string") id!: string;                  // "creature-<n>" monotonic
  @type("string") creatureType!: string;        // KTS unit name
  @type("number") x: number = 0;
  @type("number") z: number = 0;
  @type("number") targetX: number = 0;
  @type("number") targetZ: number = 0;
  @type("number") facing: number = 0;           // radians, 0 = +X
  @type("uint16") hp: number = 1;
  @type("uint16") hpMax: number = 1;
  @type("string") aiState: string = "idle";

  // === Pet ownership (Slice 1) — a pet is "a creature with an owner". ===
  // ownerId="" => wild creature (every spawner path). Set by tameAnimal().
  // teamId mirrors the owner's team; the allied-pet guard in the attack route
  // (HelloRoom) + the explicit-lock path in swing.ts refuse to acquire any
  // creature where ownerId!=="" && teamId===self.teamId, preventing a player
  // from attacking their own or an ally's pet. (autoAcquire is cubes-only, so
  // creatures are never auto-targeted.) Wild creatures (ownerId "") and enemy
  // pets (different teamId) remain fully targetable.
  // posture is the AI mode: "Follow" (default) | "Stay" | "Attack";
  // only "Follow" is exercised in Slice 1.
  // Spec: docs/superpowers/specs/2026-06-18-pets-implementation-design.md §2.1.
  @type("string") ownerId: string = "";
  @type("uint8")  teamId: number = 0;
  @type("string") posture: string = "Follow";
  // pet level 1..PET_LEVEL_CAP; wild creatures stay 1
  @type("uint8")  level: number = 1;
  // Authoritative attack damage synced from recomputePetStats (pets only).
  // Wild creatures stay 0 — recompute is never called for them.
  @type("uint16") dmg: number = 0;
  // CC VFX marker (item-effects creature-CC). Server epoch-ms until which this
  // creature is under any incapacitation (stun / disarm / slow); 0 = none.
  // Written from the off-schema creature-incap system each tick (creatures have
  // no buff store to read CC overlays off). The client renders the stun-stars
  // overlay over a creature whose incapUntil > now. Synced as a plain number
  // (epoch-ms exceeds uint32 range, so not an integer field type).
  @type("number") incapUntil: number = 0;
  // Weapon/ability DoT VFX marker (item-effects creature-DoT). Server epoch-ms
  // until which this creature carries an active damage-over-time effect (poison /
  // fire / bash), 0 = none; `dotKind` names the dominant kind for the tint.
  // Creatures have no buff store, so this mirrors the off-schema creature-DoT
  // system. The client floater manager renders a colored poison/fire overlay +
  // tint over a creature whose dotUntil > now. Synced as a plain number
  // (epoch-ms exceeds uint32 range).
  @type("number") dotUntil: number = 0;
  @type("string") dotKind: string = "";
}

// Building entity. Server-authoritative; ticks per game/server/src/buildings/.
//
// state cycles "constructing" → "ready" → "destroyed".
// Construction is linear-fill: hp goes from 1 → hpMax over buildDuration ms.
// armorDisabled is true while constructing (KTS ArmorDisabledWhileConstructing).
// On destroyed, server emits "building-killed" then deletes from state.buildings.
//
// Wonder gets dual roles: it's a Building (HP/destruction), and the Wonder timer
// reads/writes match-state fields on HelloState (see below).
//
// `buildingType` stays a wire-typed `"string"` (mirrors `Creature.creatureType`)
// for @colyseus/schema 3.0 compat; type-narrowing happens at read sites via
// `as BuildingType` casts (BuildingType enum exported from @tribez/shared).
export class Building extends Schema {
  @type("string") id!: string;                  // "building-<n>" monotonic
  @type("string") buildingType!: string;        // BuildingType enum value
  @type("string") ownerId: string = "";         // sessionId of placer; "" only for debug
  @type("uint8")  teamId: number = 0;
  @type("number") x: number = 0;
  @type("number") z: number = 0;
  @type("uint16") hp: number = 1;
  @type("uint16") hpMax: number = 1;
  @type("string") state: string = "constructing"; // "constructing" | "ready" | "destroyed"
  @type("number") buildStartedAt: number = 0;   // server ms
  @type("number") buildDurationMs: number = 0;  // ms
  @type("boolean") armorDisabled: boolean = true;
  // Trap-only: remaining proximity charges. Default 0 (non-trap buildings ignore).
  @type("uint8")  charges: number = 0;
  // StorageOutpost-only: LockStorage owner toggle. When true, non-owners cannot
  // BUY from this outpost (selling is always allowed). Default false (open market).
  @type("boolean") locked: boolean = false;
}

/**
 * Corpse — drop-on-death bag. When a Cube dies (Phase 2 follow-up), the server
 * spawns a Corpse at the death position holding the victim's backpack contents
 * + carried gold; nearby players can loot it before `despawnAt` (server ms).
 *
 * Why a separate Schema: keeps loot UI rendering off the client's Cube list and
 * lets corpses outlive the dead Cube (the dead Cube respawns; the corpse stays).
 * `items` reuses BackpackSlot so the loot popup can render with the same UI as
 * the player backpack — no second slot type to maintain.
 *
 * Match-end / cleanup: corpses get pruned when `despawnAt < Date.now()` in the
 * room's main tick. v1 default lifetime is 60s (server constant, not on schema).
 */
export class Corpse extends Schema {
  @type("string") id!: string;                  // "corpse-<n>" monotonic
  @type("number") x: number = 0;
  @type("number") z: number = 0;
  @type("number") despawnAt: number = 0;        // server ms when this corpse vanishes
  @type("string") ownerName: string = "";       // display name of the dead player (UI hint)
  @type({ map: BackpackSlot }) items = new MapSchema<BackpackSlot>();
  @type("uint32") gold: number = 0;             // coins dropped (separate from items for HUD)
  // True for a hand-dropped inventory item (vs a death corpse). The client renders
  // drops as a plain ground item; the findable loot beacon is reserved for deaths.
  @type("boolean") isDrop: boolean = false;
}

/**
 * Lobby roster entry — one per player (human or bot) who has joined the pre-game
 * lobby. Written by the server on join/leave; read by the client lobby UI to show
 * the player list before the match starts. Keyed by sessionId in HelloState.roster.
 */
export class RosterEntry extends Schema {
  @type("string") sessionId = "";
  @type("string") name = "";
  @type("uint8") teamId = 0;
  @type("boolean") isBot = false;
}

/**
 * Minimum surface a swing target must expose. Cube and Creature both implement this
 * structurally — server-side type narrowing only; not synced to clients.
 *
 * Used by swing.ts to make creatures attackable without inheriting Cube's full
 * combat schema (HP fields are enough for kill detection; armor/crit math doesn't
 * apply to creature targets in v1 — they take raw physical damage).
 */
export interface AttackTarget {
  x: number;
  z: number;
  hp: number;
  hpMax: number;
}

export class HelloState extends Schema {
  // Fog of war (feat/fog-of-war): `cubes` is @view()-gated — each client only
  // receives the cubes its team can currently see (own + allies always; enemies
  // only while inside team vision). HelloRoom.updateVision() drives per-client
  // StateView add/remove every tick. Items never added are fully withheld (key
  // AND value), which IS the snap-invisible model (no last-seen ghost). Without
  // a StateView (e.g. a client that never set client.view), the gate is a no-op
  // and all cubes are visible — but HelloRoom sets client.view in onJoin.
  @view() @type({ map: Cube }) cubes = new MapSchema<Cube>();
  // `resources` (trees/stone/etc.) stay UNGATED — they're the static map you
  // remember once seen; gating them would need per-client explored memory on the
  // server. Creatures, enemy buildings, and corpses ARE fog-gated (hidden threats
  // / scouting). Own+ally buildings are always added back in updateVision().
  @type({ map: ResourceNode }) resources = new MapSchema<ResourceNode>();
  @view() @type({ map: Creature }) creatures = new MapSchema<Creature>();
  @view() @type({ map: Building }) buildings = new MapSchema<Building>();
  @view() @type({ map: Corpse }) corpses = new MapSchema<Corpse>();
  // Travelling basic-attack projectiles (ranged weapons). FoW-gated like
  // creatures: a client sees a projectile while either endpoint is in team
  // vision. Cleared on match reset. Never persisted.
  @view() @type({ map: Projectile }) projectiles = new MapSchema<Projectile>();
  // Match elapsed time = (now - matchStartMs) / 1000 in seconds. Used by respawnDuration
  // formula and creature scaling. Set once at room onCreate.
  @type("number") matchStartMs: number = 0;
  // Day/night cycle counter — server-authoritative.
  // dayNightPhase: 0 = day, 1 = night. Emits DAWN / DUSK on transition.
  @type("uint8") dayNightPhase: number = 0;
  @type("number") cycleElapsed: number = 0;
  // Cycle counter for the HUD clock label. Starts at 1 ("Day 1"); increments
  // on each DAWN. The night that follows "Day N" is labelled "Night N".
  @type("uint16") dayCount: number = 1;
  // Lobby configuration + roster (added for room phase machine — Task 1).
  // matchState domain: "lobby" (pre-start) | "running" (active) | "ended" (finished).
  // hostSessionId is the player who created the room and can start the match.
  // roster holds one RosterEntry per joined player/bot, keyed by sessionId.
  @type("string") hostSessionId = "";
  @type("uint8") numTeams = 2;
  @type("uint8") teamSize = 3;
  @type("boolean") allowJoinInProgress = true;
  @type("boolean") isPrivate = false;
  @type("string") lobbyName = "";
  @type({ map: RosterEntry }) roster = new MapSchema<RosterEntry>();

  // Match-end / Wonder timer state (Phase D; landed early so the schema is stable across phases)
  @type("string") matchState: string = "lobby"; // "lobby" | "running" | "ended"
  @type("uint8")  winningTeamId: number = 255;    // 0/1 once set; 255 = unset/draw
  @type("number") matchEndedAt: number = 0;       // server ms; 0 = not ended
  @type("number") wonderDeadlineMs: number = 0;   // server ms when timer expires; 0 = no active timer
  @type("boolean") wonderTimerExtended: boolean = false;
  // Per-team living-player counts (excludes team-99 dummies). Server-authored
  // each tick from match.tick so the win-readout HUD can show "enemies still
  // alive" — a top-level scalar, NOT a view-filtered entity, so it syncs to every
  // client regardless of fog-of-war (counting client-side `cubes` would miss
  // unscouted enemies and read a false 0). v1 is 2 teams (0/1).
  @type("uint8") team0Alive: number = 0;
  @type("uint8") team1Alive: number = 0;
}
